import { forwardRef } from 'react';

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { SvgIcon } from '@material-ui/core';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import type { SvgIconProps, Theme } from '@material-ui/core';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';

import classNames from 'classnames';

import {
  CIRCLE_RADIUS,
  ICON_PADDING,
  ICON_VIEWBOX,
  STROKE_WIDTH,
  getColorsClasses,
} from './constants';
import { Color } from './types';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: ICON_PADDING,
    },
    backgroundCircle: {
      opacity: 0.5,
    },
    ...getColorsClasses(theme),
  });

type LiveProps = Omit<SvgIconProps, 'classes' | 'children' | 'color'> &
  WithStyles<typeof styles> & {
    color?: Color;
    error?: boolean;
    progress?: number;
  };

const LiveIcon = forwardRef<SVGSVGElement, LiveProps>((props, ref) => {
  const {
    classes,
    className,
    error,
    color: colorProp = error ? 'error' : 'primary',
    progress = 1,
    ...otherProps
  } = props;

  // For progress = 1, we need to draw a complete circle using two arcs
  // For progress = 0, show a small arc (5% of the circle)
  const pathD =
    progress >= 1
      ? `M 12 2 A ${CIRCLE_RADIUS} ${CIRCLE_RADIUS} 0 1 1 12 22 A ${CIRCLE_RADIUS} ${CIRCLE_RADIUS} 0 1 1 12 2`
      : (() => {
          const minProgress = 0.05; // 5% minimum
          const angle = Math.max(minProgress, progress) * Math.PI * 2;
          const endX = 12 + CIRCLE_RADIUS * Math.sin(angle);
          const endY = 12 - CIRCLE_RADIUS * Math.cos(angle);
          const largeArcFlag = progress > 0.5 ? 1 : 0;
          return `M 12 2 A ${CIRCLE_RADIUS} ${CIRCLE_RADIUS} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
        })();

  return (
    <SvgIcon
      ref={ref}
      viewBox={ICON_VIEWBOX}
      className={classNames(
        classes.root,
        classes[`${colorProp}Color`],
        className,
      )}
      {...otherProps}
    >
      <circle
        cx="12"
        cy="12"
        r={CIRCLE_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH}
        className={classes.backgroundCircle}
      />
      <path
        d={pathD}
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH}
      />
      {error ? (
        <path
          d="M 12 7 L 12 13 M 12 15 L 12 17"
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE_WIDTH * 1.25}
          strokeLinecap="butt"
        />
      ) : (
        <circle
          cx="12"
          cy="12"
          r="3"
          fill="currentColor"
          strokeWidth={STROKE_WIDTH * 2}
        />
      )}
    </SvgIcon>
  );
});

LiveIcon.displayName = 'StateIcon.RunningIcon';

export default withStyles(styles)(LiveIcon);
